import React from 'react';
import { useGetAllVendorSettingsQuery, useGetKnownVendorsQuery } from '../../store/Api';
import { Dropdown, IDropdownOption, ISelectableOption, Label, TextField } from '@fluentui/react';
import { IKnownVendor } from '../../models/Settings';
import { SettingId } from '../../settings/registry';

interface IVendorSelectionDropdownProps {
    selectedVendor?: string;
    onChange: (vendor: string) => void;
    allowCustom?: boolean;
    label?: string;
    disabled?: boolean;
    onBlur?: () => void;
    /** When true, options are known vendors NOT already configured by the org (Add Vendor flow). Default: org vendors ∪ known vendors. */
    knownVendorsOnly?: boolean;
    /** Fires with the matched known vendor when the selection is one, undefined otherwise (for prefill). */
    onKnownVendorSelected?: (vendor: IKnownVendor | undefined) => void;
    /** Registry identifier, so documentation can deep-link to this field. */
    settingId?: SettingId;
}

const CUSTOM_VENDOR_KEY = 'CUSTOM';

const VendorSelectionDropdown: React.FC<IVendorSelectionDropdownProps> = (props) => {
    const vendors = useGetAllVendorSettingsQuery();
    const knownVendors = useGetKnownVendorsQuery();
    const [selectedVendor, setSelectedVendor] = React.useState<string | undefined>(props.selectedVendor);
    const [customVendor, setCustomVendor] = React.useState<string>('');
    const [customEnabled, setCustomEnabled] = React.useState<boolean>(false);

    // The set of vendors the user can pick, excluding the Custom sentinel. Known vendors already configured
    // by the org are dropped (case-insensitive dedupe, matching API behavior); org entries win on duplicates.
    // A failed/loading known-vendors query degrades to [] so it never breaks the default-mode dropdown.
    const selectableOptions = React.useMemo<ISelectableOption[]>(() => {
        const orgItems = vendors.data?.items ?? [];
        const orgKeys = new Set(orgItems.map(v => v.sourceVendor.toLowerCase()));
        const orgOptions = orgItems.map(v => ({key: v.sourceVendor, text: v.vendorDisplayName ?? v.sourceVendor}));
        const knownNotInOrg = (knownVendors.data?.items ?? [])
            .filter(k => !orgKeys.has(k.sourceVendor.toLowerCase()))
            .map(k => ({key: k.sourceVendor, text: k.vendorDisplayName ?? k.sourceVendor}));
        return props.knownVendorsOnly ? knownNotInOrg : [...orgOptions, ...knownNotInOrg];
    }, [vendors.data, knownVendors.data, props.knownVendorsOnly]);

    const options = React.useMemo<ISelectableOption[]>(() => [
        ...selectableOptions,
        ...(props.allowCustom ? [{key: CUSTOM_VENDOR_KEY, text: 'Custom'}] : [])
    ], [selectableOptions, props.allowCustom]);

    // Flip to custom mode when the persisted selection isn't one of the selectable options. Search the full
    // selectable union and wait until both queries have settled, otherwise a persisted known-vendor selection
    // would transiently render as Custom before its options load. The match must be exact (not case-insensitive)
    // because Dropdown.selectedKey matches exactly - a case-mismatched value must fall through to Custom.
    React.useEffect(() => {
        if (vendors.isLoading || knownVendors.isLoading) return;
        const match = selectableOptions.find(o => o.key?.toString() === props.selectedVendor);
        if (!customEnabled && match) setSelectedVendor(props.selectedVendor);
        else if (props.selectedVendor) {
            setCustomEnabled(true);
            setCustomVendor(props.selectedVendor);
        }
    }, [props.selectedVendor, vendors.isLoading, knownVendors.isLoading, selectableOptions]);

    // Syncing local state back out is only meaningful once something here has actually changed. The
    // first run is just the mount echoing props back (and, before customEnabled settles, briefly a
    // blank vendor) plus a blur nobody performed — which auto-saving parents hear as an edit, so a
    // panel saved the folio merely for being opened.
    const announceReady = React.useRef(false);
    React.useEffect(() => {
        if (!announceReady.current) {
            announceReady.current = true;
            return;
        }
        if (customEnabled) {
            props.onChange(customVendor);
        } else {
            props.onChange(selectedVendor ?? '');
            props.onBlur?.();
        }
    }, [selectedVendor, customVendor, customEnabled]);

    const updateDropdown = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        setSelectedVendor(option?.key?.toString() ?? '');
        if (option?.key !== CUSTOM_VENDOR_KEY) {
            const key = option?.key?.toString() ?? '';
            props.onChange(key);
            setCustomEnabled(false);
            const match = knownVendors.data?.items.find(k => k.sourceVendor.toLowerCase() === key.toLowerCase());
            props.onKnownVendorSelected?.(match);
        } else {
            setCustomEnabled(true);
            props.onKnownVendorSelected?.(undefined);
        }
    }

    // In default mode the org query is authoritative for enabling the dropdown; in knownVendorsOnly mode both
    // queries are needed (exclusion without org data would transiently show already-configured vendors).
    const dataMissing = props.knownVendorsOnly ? (!knownVendors.data || !vendors.data) : !vendors.data;

    return <div data-setting-id={props.settingId}>
        <Label>{props.label ?? 'Vendor'}</Label>
        <Dropdown
            disabled={dataMissing || props.disabled}
            options={options}
            selectedKey={customEnabled ? 'CUSTOM' : props.selectedVendor}
            onChange={updateDropdown}
        />
        {customEnabled && !vendors.isLoading && <div style={{paddingTop: '1em'}}>
            <TextField
                disabled={dataMissing || props.disabled}
                value={customVendor}
                onChange={(e, v) => setCustomVendor(v ?? '')}
                onBlur={props.onBlur}
            />
        </div>}
    </div>;
}

export default VendorSelectionDropdown;
